import { useState, useRef, useEffect, type FormEvent } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Send, Pin } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'

interface Message {
  id: string
  channelId: string
  authorId: string
  authorType: string
  body: string
  pinned: boolean
  attachmentUrl: string | null
  createdAt: string
  editedAt: string | null
}

interface ChannelDetail {
  id: string
  name: string
  scope: string
  muted: boolean
  archived: boolean
  memberCount: number
  messageCount: number
  members: Array<{
    crew: { id: string; displayName: string; primaryRole: string; photoUrl: string | null }
  }>
}

export function CrewChatPage() {
  const { channelId } = useParams<{ channelId: string }>()
  const qc = useQueryClient()
  const [newMessage, setNewMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { data: channel } = useQuery<ChannelDetail, ApiClientError>({
    queryKey: ['crew-channel', channelId],
    queryFn: () => apiFetch(`/crew/profile/channels/${channelId}`),
    enabled: !!channelId,
  })

  const { data: messagesData } = useQuery<Message[], ApiClientError>({
    queryKey: ['crew-channel-messages', channelId],
    queryFn: () => apiFetch(`/crew/profile/channels/${channelId}/messages`),
    enabled: !!channelId,
    refetchInterval: 10_000, // poll every 10s
  })

  const messages = messagesData ?? []

  // Mark as read on mount and when messages change
  useEffect(() => {
    if (channelId) {
      apiFetch(`/crew/profile/channels/${channelId}/read`, { method: 'POST' }).catch(() => {})
    }
  }, [channelId, messages.length])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const sendMutation = useMutation({
    mutationFn: (body: string) =>
      apiFetch(`/crew/profile/channels/${channelId}/messages`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: () => {
      setNewMessage('')
      qc.invalidateQueries({ queryKey: ['crew-channel-messages', channelId] })
      qc.invalidateQueries({ queryKey: ['crew-channels'] })
    },
  })

  function handleSend(e: FormEvent) {
    e.preventDefault()
    const trimmed = newMessage.trim()
    if (!trimmed || sendMutation.isPending) return
    sendMutation.mutate(trimmed)
  }

  // Build author name lookup from channel members
  const authorNames: Record<string, string> = {}
  if (channel?.members) {
    for (const m of channel.members) {
      authorNames[m.crew.id] = m.crew.displayName
    }
  }

  const pinnedMessages = messages.filter((m) => m.pinned)

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-ovation-ink/10 px-4 py-3">
        <Link
          to="/crew/messages"
          className="rounded-md p-1 hover:bg-ovation-ink/5"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="min-w-0">
          <h1 className="truncate font-medium text-ovation-ink">
            {channel?.name ?? 'Loading...'}
          </h1>
          <p className="text-xs text-ovation-muted">
            {channel?.memberCount ?? 0} members
            {channel?.muted && ' · muted'}
            {channel?.archived && ' · archived'}
          </p>
        </div>
      </div>

      {/* Pinned messages */}
      {pinnedMessages.length > 0 && (
        <div className="border-b border-ovation-ink/10 bg-amber-50 px-4 py-2">
          {pinnedMessages.map((m) => (
            <div key={m.id} className="flex items-start gap-2 text-xs">
              <Pin className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
              <div>
                <span className="font-medium text-amber-800">
                  {m.authorType === 'admin' ? 'Admin' : authorNames[m.authorId] ?? 'Crew'}:
                </span>{' '}
                <span className="text-amber-700">{m.body}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-3">
          {messages.map((msg) => {
            const isAdmin = msg.authorType === 'admin'
            const name = isAdmin ? 'Admin' : authorNames[msg.authorId] ?? 'Crew'

            return (
              <div key={msg.id} className="flex flex-col">
                <div className="flex items-baseline gap-2">
                  <span className={`text-xs font-semibold ${isAdmin ? 'text-blue-700' : 'text-ovation-ink'}`}>
                    {name}
                  </span>
                  <span className="text-[10px] text-ovation-muted">
                    {new Date(msg.createdAt).toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {msg.pinned && <Pin className="h-2.5 w-2.5 text-amber-500" />}
                </div>
                <p className="text-sm text-ovation-ink">{msg.body}</p>
              </div>
            )
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      {!channel?.archived && (
        <form onSubmit={handleSend} className="border-t border-ovation-ink/10 px-4 py-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={channel?.muted ? 'Channel is muted' : 'Type a message...'}
              disabled={channel?.muted}
              className="flex-1 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sendMutation.isPending || channel?.muted}
              className="rounded-md bg-ovation-ink px-3 py-2 text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
